import React, { useState, useEffect } from 'react';
import { useTheme, getT, getBrandColors} from '../ThemeContext';
import PageHeaderIcon from "../components/PageHeaderIcon";
import { PrimaryButton, GhostButton } from "../components/ThemedButton";
import API_BASE from '../apiConfig';
import { getSessionUser } from '../utils/session';
import { useDateTimeFormat } from '../utils/formatters';
import { FaTrophy, FaCheckCircle, FaExclamationCircle, FaSearch } from 'react-icons/fa';

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";
const BLUR = "blur(18px)";

// The "other" per-day / per-item costs (Total Cost + Squad Size + derived Cost/Game are handled separately)
const COST_FIELDS = [
  { key: 'coach_cost_per_day', label: 'Coach / Day' },
  { key: 'umpire_cost_1_per_day', label: 'Umpire 1 / Day' },
  { key: 'umpire_cost_2_per_day', label: 'Umpire 2 / Day' },
  { key: 'ball_cost', label: 'Ball' },
  { key: 'medal_cost', label: 'Medal' },
  { key: 'miscellaneous_cost', label: 'Misc' },
];

const TournamentCostPlanner = ({ onNavigate }) => {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { fmtDate } = useDateTimeFormat();
  const user = getSessionUser();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [ageCategories, setAgeCategories] = useState([]);
  const [done, setDone] = useState(false);

  const [mode, setMode] = useState('create'); // 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [allTournaments, setAllTournaments] = useState([]);
  const [listSort, setListSort] = useState({ key: null, dir: 'asc' });

  const [formData, setFormData] = useState({
    tournament_name: '',
    number_of_days: 3,
    registration_deadline: '',
    start_date: '',
    end_date: '',
    age_categories: [],
  });

  const [dates, setDates] = useState([]);
  const [costs, setCosts] = useState({});

  useEffect(() => { loadAgeCategories(); handleNumDaysChange(3); }, []); // eslint-disable-line

  const loadAgeCategories = async () => {
    try {
      const response = await fetch(`${BASE}/tournament/config/age-categories`);
      const data = await response.json();
      if (data.success) setAgeCategories(data.categories || []);
    } catch (err) { console.error('Error loading age categories:', err); }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAgeCategoryToggle = (categoryId) => {
    setFormData(prev => {
      const active = prev.age_categories.includes(categoryId);
      const age_categories = active
        ? prev.age_categories.filter(id => id !== categoryId)
        : [...prev.age_categories, categoryId];
      return { ...prev, age_categories };
    });
    setCosts(prev => {
      if (prev[categoryId]) return prev;
      return { ...prev, [categoryId]: { age_category_id: categoryId, squad_size: 11 } };
    });
  };

  const handleNumDaysChange = (num) => {
    setFormData(prev => ({ ...prev, number_of_days: num }));
    setDates(prev => {
      const next = [];
      for (let i = 1; i <= num; i++) {
        const existing = prev.find(d => d.day_number === i);
        next.push(existing || { day_number: i, tournament_date: '', notes: '' });
      }
      return next;
    });
  };

  const handleDateChange = (dayNumber, value) => {
    setDates(prev => prev.map(d => d.day_number === dayNumber ? { ...d, tournament_date: value } : d));
  };

  const handleCostChange = (categoryId, field, value) => {
    setCosts(prev => ({
      ...prev,
      [categoryId]: { ...(prev[categoryId] || {}), age_category_id: categoryId, [field]: parseFloat(value) || 0 },
    }));
  };

  const createAndPublish = async (publish) => {
    if (!formData.tournament_name.trim()) { setError('Tournament name is required'); return; }
    if (formData.age_categories.length === 0) { setError('Select at least one age category'); return; }
    if (!formData.registration_deadline || !formData.start_date || !formData.end_date) { setError('Please fill in all dates'); return; }
    if (dates.some(d => !d.tournament_date)) { setError('Please select a date for each tournament day'); return; }

    setLoading(true); setError(''); setSuccess('');
    try {
      const createRes = await fetch(`${BASE}/tournament`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, user_id: user.user_id }),
      });
      const createData = await createRes.json();
      if (!createData.success) { setError(createData.message || createData.detail || 'Error creating tournament'); setLoading(false); return; }
      const tournamentId = createData.tournament_id;

      const datesRes = await fetch(`${BASE}/tournament/${tournamentId}/dates`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dates }),
      });
      const datesData = await datesRes.json();
      if (!datesData.success) { setError(datesData.message || datesData.detail || 'Error adding dates'); setLoading(false); return; }

      const costsArray = formData.age_categories.map(catId => costs[catId] || { age_category_id: catId });
      const costsRes = await fetch(`${BASE}/tournament/${tournamentId}/costs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ costs: costsArray }),
      });
      const costsData = await costsRes.json();
      if (!costsData.success) { setError(costsData.message || costsData.detail || 'Error saving costs'); setLoading(false); return; }

      if (publish) {
        const pubRes = await fetch(`${BASE}/tournament/${tournamentId}/publish`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: user.user_id }),
        });
        const pubData = await pubRes.json();
        if (!pubData.success) { setError(pubData.message || pubData.detail || 'Error publishing tournament'); setLoading(false); return; }
        setSuccess(`"${formData.tournament_name}" created and published.`);
      } else {
        setSuccess(`"${formData.tournament_name}" saved as draft.`);
      }
      setDone(true);

      // Hand the new tournament off to the Scheduler so it's pre-selected there.
      sessionStorage.setItem('scheduler_prefill_tournament_id', String(tournamentId));
      if (onNavigate) {
        setTimeout(() => onNavigate('Tournament Scheduler'), 900);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ tournament_name: '', number_of_days: 3, registration_deadline: '', start_date: '', end_date: '', age_categories: [] });
    setDates([]); setCosts({}); setError(''); setSuccess(''); setDone(false);
    setEditingId(null); setSearch('');
    handleNumDaysChange(3);
  };

  const switchMode = (m) => {
    setMode(m);
    resetForm();
    if (m === 'edit') loadAllTournaments();
  };

  const loadAllTournaments = async () => {
    try {
      const res = await fetch(`${BASE}/tournament?limit=100`);
      const data = await res.json();
      if (data.success) setAllTournaments(data.tournaments || []);
    } catch (err) { console.error(err); }
  };

  const loadForEdit = async (t) => {
    setSearch(t.tournament_name); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE}/tournament/${t.tournament_id}`);
      const data = await res.json();
      if (!data.success) { setError(data.message || 'Failed to load tournament'); return; }
      const tour = data.tournament;
      setEditingId(tour.tournament_id);
      const deadline = tour.registration_deadline ? tour.registration_deadline.replace(' ', 'T').slice(0, 16) : '';
      setFormData({
        tournament_name: tour.tournament_name || '',
        number_of_days: tour.number_of_days || (data.dates || []).length || 1,
        registration_deadline: deadline,
        start_date: tour.start_date || '',
        end_date: tour.end_date || '',
        age_categories: (data.age_categories || []).map(c => c.age_category_id),
      });
      setDates((data.dates || []).map(d => ({ day_number: d.day_number, tournament_date: d.tournament_date, notes: d.notes || '' })));
      const costObj = {};
      (data.costs || []).forEach(c => {
        costObj[c.age_category_id] = {
          age_category_id: c.age_category_id,
          tournament_cost_per_player: Number(c.tournament_cost_per_player) || 0,
          squad_size: Number(c.squad_size) || 11,
          coach_cost_per_day: Number(c.coach_cost_per_day) || 0,
          umpire_cost_1_per_day: Number(c.umpire_cost_1_per_day) || 0,
          umpire_cost_2_per_day: Number(c.umpire_cost_2_per_day) || 0,
          ball_cost: Number(c.ball_cost) || 0,
          medal_cost: Number(c.medal_cost) || 0,
          miscellaneous_cost: Number(c.miscellaneous_cost) || 0,
        };
      });
      setCosts(costObj);
    } catch (err) { setError(err.message); }
  };

  const saveChanges = async () => {
    if (!formData.tournament_name.trim()) { setError('Tournament name is required'); return; }
    if (formData.age_categories.length === 0) { setError('Select at least one age category'); return; }
    if (!formData.registration_deadline || !formData.start_date || !formData.end_date) { setError('Please fill in all dates'); return; }
    if (dates.some(d => !d.tournament_date)) { setError('Please select a date for each tournament day'); return; }

    setLoading(true); setError(''); setSuccess('');
    try {
      const putRes = await fetch(`${BASE}/tournament/${editingId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.user_id,
          tournament_name: formData.tournament_name,
          number_of_days: formData.number_of_days,
          registration_deadline: formData.registration_deadline,
          start_date: formData.start_date,
          end_date: formData.end_date,
        }),
      });
      const putData = await putRes.json();
      if (!putData.success) { setError(putData.message || putData.detail || 'Error updating tournament'); setLoading(false); return; }

      const catRes = await fetch(`${BASE}/tournament/${editingId}/categories`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id, age_categories: formData.age_categories }),
      });
      const catData = await catRes.json();
      if (!catData.success) { setError(catData.message || catData.detail || 'Error updating categories'); setLoading(false); return; }

      const datesRes = await fetch(`${BASE}/tournament/${editingId}/dates`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dates }),
      });
      const datesData = await datesRes.json();
      if (!datesData.success) { setError(datesData.message || datesData.detail || 'Error updating dates'); setLoading(false); return; }

      const costsArray = formData.age_categories.map(catId => costs[catId] || { age_category_id: catId });
      const costsRes = await fetch(`${BASE}/tournament/${editingId}/costs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ costs: costsArray }),
      });
      const costsData = await costsRes.json();
      if (!costsData.success) { setError(costsData.message || costsData.detail || 'Error saving costs'); setLoading(false); return; }

      let msg = `"${formData.tournament_name}" updated.`;
      if ((catData.skipped || []).length) msg += ` Note: ${catData.skipped.length} category(ies) kept because they already have registrations.`;
      setSuccess(msg);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Sorting for the "Edit Existing" tournament list ──
  const toggleListSort = (key) => setListSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  const listArrow = (key) => listSort.key === key ? (listSort.dir === 'asc' ? ' ▲' : ' ▼') : '';
  const listSortVal = (t) => {
    switch (listSort.key) {
      case 'name': return (t.tournament_name || '').toLowerCase();
      case 'status': return t.status || '';
      case 'start': return t.start_date || '';
      case 'end': return t.end_date || '';
      case 'days': return Number(t.number_of_days) || 0;
      default: return '';
    }
  };
  const visibleTournaments = (() => {
    const filtered = allTournaments.filter(t => t.tournament_name.toLowerCase().includes(search.toLowerCase()));
    if (!listSort.key) return filtered;
    return [...filtered].sort((a, b) => {
      const va = listSortVal(a), vb = listSortVal(b);
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return listSort.dir === 'asc' ? cmp : -cmp;
    });
  })();

  const glassCard = {
    background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
    border: T.glassBdr, borderRadius: '14px', boxShadow: T.glassSh,
    padding: '18px 22px', boxSizing: 'border-box', marginBottom: '14px',
  };
  const labelStyle = {
    display: 'block', marginBottom: '6px', fontSize: '10.5px', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textLabel, fontFamily: FONT,
  };
  const inputStyle = {
    border: `1.5px solid ${T.inputBdr}`, borderRadius: '8px', padding: '8px 12px',
    background: T.inputBg, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
    width: '100%', boxSizing: 'border-box', outline: 'none', fontSize: '13px',
    color: T.inputText, fontFamily: FONT,
  };
  const chip = (active) => ({
    padding: '7px 14px', border: `2px solid ${active ? getBrandColors().yellow : T.inputBdr}`,
    borderRadius: '7px', backgroundColor: active ? 'rgba(247,206,11,0.15)' : T.glass,
    color: active ? getBrandColors().yellow : T.textLabel, cursor: 'pointer', fontWeight: 700,
    fontSize: '12.5px', fontFamily: FONT,
  });
  const costInputStyle = {
    width: '100%', padding: '9px 8px', border: `1.5px solid ${T.inputBdr}`, borderRadius: '7px',
    background: T.inputBg, color: T.inputText, fontSize: '13px', outline: 'none', fontFamily: FONT,
    textAlign: 'right', boxSizing: 'border-box',
  };
  const thCost = {
    padding: '6px 8px', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
    color: T.textLabel, fontFamily: FONT, textAlign: 'right', whiteSpace: 'nowrap',
  };
  const tdCost = { padding: '5px 8px', textAlign: 'right', color: T.textPrimary, fontFamily: FONT, fontSize: '13px', minWidth: '90px' };

  if (done) {
    return (
      <div style={{ minHeight: '100dvh', padding: '20px 28px 48px', boxSizing: 'border-box' }}>
        <div style={{ ...glassCard, textAlign: 'center', padding: '48px 24px' }}>
          <FaCheckCircle size={40} color={getBrandColors().green} style={{ marginBottom: '16px' }} />
          <h2 style={{ fontSize: '19px', fontWeight: 700, color: T.textPrimary, margin: '0 0 8px', fontFamily: FONT }}>
            {success}
          </h2>
          {mode === 'create' && onNavigate ? (
            <p style={{ fontSize: '13px', color: T.textMuted, margin: '0 0 16px', fontFamily: FONT }}>
              Taking you to the Scheduler to build the squad...
            </p>
          ) : (
            <PrimaryButton onClick={() => { resetForm(); if (mode === 'edit') loadAllTournaments(); }} style={{ marginTop: '16px' }}>
              {mode === 'edit' ? 'Edit Another Tournament' : 'Create Another Tournament'}
            </PrimaryButton>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', padding: '18px 26px 40px', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <PageHeaderIcon icon={<FaTrophy size={19} color={getBrandColors().blueDark} />} />
        <h1 style={{ fontSize: '22px', fontWeight: 800, color: T.textPrimary, margin: 0, fontFamily: FONT }}>
          Tournament Cost Planner
        </h1>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          <button onClick={() => switchMode('create')} style={chip(mode === 'create')}>Create New</button>
          <button onClick={() => switchMode('edit')} style={chip(mode === 'edit')}>Edit Existing</button>
        </div>
      </div>

      {error && (
        <div style={{ display: 'flex', gap: '10px', padding: '10px 14px', borderRadius: '9px', marginBottom: '14px',
          fontSize: '12.5px', fontWeight: 600, fontFamily: FONT, background: 'rgba(248,113,113,0.12)', color: getBrandColors().red,
          border: '1px solid rgba(248,113,113,0.30)' }}>
          <FaExclamationCircle size={14} /><span>{error}</span>
        </div>
      )}

      {mode === 'edit' && !editingId && (
        <div style={glassCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Select a tournament to edit</label>
            <div style={{ position: 'relative', minWidth: '240px' }}>
              <FaSearch size={12} color={T.textMuted} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by name..." style={{ ...inputStyle, paddingLeft: '32px' }} />
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.inputBdr}` }}>
                  <th style={{ ...thCost, textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleListSort('name')}>Tournament{listArrow('name')}</th>
                  <th style={{ ...thCost, textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleListSort('status')}>Status{listArrow('status')}</th>
                  <th style={{ ...thCost, textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleListSort('start')}>Start{listArrow('start')}</th>
                  <th style={{ ...thCost, textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleListSort('end')}>End{listArrow('end')}</th>
                  <th style={{ ...thCost, textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleListSort('days')}>Days{listArrow('days')}</th>
                  <th style={{ ...thCost, textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleTournaments
                  .map((t, idx) => (
                    <tr key={t.tournament_id} style={{ background: idx % 2 ? T.glass : 'transparent', borderBottom: `1px solid ${T.inputBdr}` }}>
                      <td style={{ ...tdCost, textAlign: 'left', fontWeight: 700 }}>{t.tournament_name}</td>
                      <td style={{ ...tdCost, textAlign: 'center' }}>
                        <span style={{ padding: '2px 9px', borderRadius: '6px', fontSize: '10.5px', fontWeight: 700,
                          background: t.status === 'PUBLISHED' ? 'rgba(52,211,153,0.14)' : 'rgba(156,163,175,0.14)',
                          color: t.status === 'PUBLISHED' ? getBrandColors().green : T.textMuted }}>{t.status}</span>
                      </td>
                      <td style={{ ...tdCost, textAlign: 'center' }}>{fmtDate(t.start_date)}</td>
                      <td style={{ ...tdCost, textAlign: 'center' }}>{fmtDate(t.end_date)}</td>
                      <td style={{ ...tdCost, textAlign: 'center' }}>{t.number_of_days}</td>
                      <td style={{ ...tdCost, textAlign: 'center' }}>
                        <button onClick={() => loadForEdit(t)} style={{ padding: '5px 14px', border: 'none', borderRadius: '7px', background: getBrandColors().yellow, color: getBrandColors().blueDark, fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Edit</button>
                      </td>
                    </tr>
                  ))}
                {allTournaments.length === 0 && (
                  <tr><td colSpan={6} style={{ ...tdCost, textAlign: 'center', color: T.textMuted, padding: '20px' }}>No tournaments found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {mode === 'edit' && editingId && (
        <GhostButton onClick={() => { setEditingId(null); setSearch(''); }} style={{ marginBottom: '12px' }}>← Back to tournament list</GhostButton>
      )}

      {(mode === 'create' || editingId) && (
      <>
      <div style={glassCard}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
          <div>
            <label style={labelStyle}>Tournament Name *</label>
            <input type="text" name="tournament_name" value={formData.tournament_name} onChange={handleFormChange}
              placeholder="e.g., Spring Cricket Championship" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Registration Deadline *</label>
            <input type="datetime-local" name="registration_deadline" value={formData.registration_deadline} onChange={handleFormChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Start Date *</label>
            <input type="date" name="start_date" value={formData.start_date} onChange={handleFormChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>End Date *</label>
            <input type="date" name="end_date" value={formData.end_date} onChange={handleFormChange} style={inputStyle} />
          </div>
        </div>

        <label style={labelStyle}>Tournament Duration</label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
          {[1, 2, 3, 4, 5, 7, 8, 10].map(num => (
            <button key={num} onClick={() => handleNumDaysChange(num)} style={chip(formData.number_of_days === num)}>
              {num} day{num > 1 ? 's' : ''}
            </button>
          ))}
        </div>

        <label style={labelStyle}>Day-by-Day Dates</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px', marginBottom: '14px' }}>
          {dates.map(d => (
            <div key={d.day_number}>
              <span style={{ fontSize: '10px', color: T.textMuted, fontFamily: FONT }}>Day {d.day_number}</span>
              <input type="date" value={d.tournament_date} onChange={(e) => handleDateChange(d.day_number, e.target.value)} style={inputStyle} />
            </div>
          ))}
        </div>

        <label style={labelStyle}>Age Categories * (select at least one)</label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {ageCategories.map(cat => (
            <button key={cat.age_category_id} onClick={() => handleAgeCategoryToggle(cat.age_category_id)}
              style={chip(formData.age_categories.includes(cat.age_category_id))}>
              {formData.age_categories.includes(cat.age_category_id) ? '✓ ' : ''}{cat.category_name}
            </button>
          ))}
        </div>
      </div>

      {formData.age_categories.length > 0 && (
        <div style={glassCard}>
          <label style={{ ...labelStyle, marginBottom: '4px' }}>Cost Structure</label>
          <p style={{ fontSize: '11px', color: T.textMuted, margin: '0 0 12px', fontFamily: FONT }}>
            Cost per game = Total Cost ÷ Squad Size. This per-game rate is what each player is charged per day they play.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.inputBdr}` }}>
                  <th style={{ ...thCost, textAlign: 'left' }}>Category</th>
                  <th style={thCost}>Total Cost</th>
                  <th style={thCost}>Squad Size</th>
                  <th style={{ ...thCost, color: getBrandColors().yellow }}>Cost / Game</th>
                  {COST_FIELDS.map(f => <th key={f.key} style={thCost}>{f.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {formData.age_categories.map(catId => {
                  const cat = ageCategories.find(c => c.age_category_id === catId);
                  const costData = costs[catId] || {};
                  const squad = Number(costData.squad_size) || 11;
                  const pool = Number(costData.tournament_cost_per_player) || 0;
                  const perGame = squad > 0 ? pool / squad : 0;
                  return (
                    <tr key={catId} style={{ borderBottom: `1px solid ${T.inputBdr}` }}>
                      <td style={{ ...tdCost, fontWeight: 700, textAlign: 'left' }}>{cat?.category_name}</td>
                      <td style={tdCost}>
                        <input type="number" step="0.01" min="0" value={costData.tournament_cost_per_player || ''}
                          onChange={(e) => handleCostChange(catId, 'tournament_cost_per_player', e.target.value)}
                          placeholder="0" style={costInputStyle} />
                      </td>
                      <td style={tdCost}>
                        <input type="number" step="1" min="1" value={costData.squad_size ?? 11}
                          onChange={(e) => handleCostChange(catId, 'squad_size', e.target.value)}
                          placeholder="11" style={costInputStyle} />
                      </td>
                      <td style={{ ...tdCost, textAlign: 'right', fontWeight: 800, color: getBrandColors().yellow }}>
                        ${perGame.toFixed(2)}
                      </td>
                      {COST_FIELDS.map(f => (
                        <td key={f.key} style={tdCost}>
                          <input type="number" step="0.01" min="0" value={costData[f.key] || ''}
                            onChange={(e) => handleCostChange(catId, f.key, e.target.value)}
                            placeholder="0" style={costInputStyle} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {mode === 'edit' ? (
        <div style={{ display: 'flex', gap: '10px' }}>
          <PrimaryButton onClick={saveChanges} disabled={loading}>
            {loading ? 'Saving...' : '✓ Save Changes'}
          </PrimaryButton>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '10px' }}>
          <GhostButton onClick={() => createAndPublish(false)} disabled={loading}>
            {loading ? 'Saving...' : 'Save as Draft'}
          </GhostButton>
          <PrimaryButton onClick={() => createAndPublish(true)} disabled={loading}>
            {loading ? 'Publishing...' : '✓ Create & Publish Tournament'}
          </PrimaryButton>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default TournamentCostPlanner;
